'use client';

import { HStack, Box } from '@terra/ui-styled-system';
import { Tooltip } from '../../components/tooltip';
import { BodyDefault } from '../../components/typography';
import { HelpCircleIcon } from '@terra/ui-icons';
import type { SettingsNavItemConfig } from './types';

const SELECTED_ITEM_BG = 'bg.neutral.subtle';
const HOVER_ITEM_BG = 'bg.neutral.hover';

export type SettingsNavItemProps = SettingsNavItemConfig & {
  selected?: boolean;
  onSelect?: (value: string) => void;
};
export function SettingsNavItem({
  value,
  label,
  badge,
  tooltip,
  disabled,
  selected,
  onSelect,
}: SettingsNavItemProps) {
  const content = (
    <HStack
      as="button"
      gap={1}
      h={9}
      w="full"
      alignItems="center"
      justifyContent="flex-start"
      px={3}
      py={2}
      borderRadius="sm"
      bg={selected ? SELECTED_ITEM_BG : 'transparent'}
      color={selected ? 'fg.neutral.default' : 'fg.neutral.muted'}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.5 : 1}
      textAlign="left"
      onClick={(event) => {
        event.preventDefault();
        if (!disabled) {
          onSelect?.(value);
        }
      }}
      aria-disabled={disabled}
      _hover={
        disabled
          ? undefined
          : { bg: selected ? SELECTED_ITEM_BG : HOVER_ITEM_BG }
      }
    >
      <Box
        display="inline-flex"
        alignItems="center"
        gap={1}
        minW={0}
        maxW="full"
      >
        {typeof label === 'string' ? (
          <BodyDefault
            as="span"
            color="inherit"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {label}
          </BodyDefault>
        ) : (
          label
        )}
        {tooltip ? (
          <Box flexShrink={0} display="flex" color="fg.neutral.muted">
            <HelpCircleIcon size={16} />
          </Box>
        ) : null}
      </Box>
      {badge ? (
        <Box flexShrink={0} ml="auto">
          {badge}
        </Box>
      ) : null}
    </HStack>
  );

  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip title={tooltip} positioning={{ strategy: 'fixed' }}>
      {content}
    </Tooltip>
  );
}
